import {
    createContext,
    useEffect,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
    type FC,
    type ReactNode,
} from "react";
import {
    DEFAULT_STREAM_PATH_OVERLAY_PREFS,
    streamPathOverlayPrefsToCameraConfig,
    type StreamPathOverlayPreferences,
} from "lib/consts/streamPathOverlay";
import type { StreamPathCameraConfig } from "lib/navigation/projectPlannedPathToVideo";
import { getBboxServiceUrl } from "lib/utils/connection";
import { authedFetch } from "lib/utils/authedFetch";
import { useARC } from "providers/ARC/ARCProvider";
import useIsHttpSecure from "lib/utils/useIsHttpSecure";
import useFleetArcId from "lib/utils/useFleetArcId";
import {
    applyResolvedColorMode,
    loadStoredColorMode,
    persistColorMode,
    resolveColorMode,
    type ColorModePreference,
    type ResolvedColorMode,
} from "lib/theme/colorMode";

/** Default matches historical StreamView overlay (teal/green). */
export const DEFAULT_BBOX_OVERLAY_COLOR = "#00e096";

const BBOX_COLOR_STORAGE_KEY = "webapp2.accessibility.bboxOverlayColor";
const STREAM_PATH_STORAGE_KEY = "webapp2.accessibility.streamPathOverlay";

function isHexColor(s: string): boolean {
    return /^#[0-9A-Fa-f]{6}$/.test(s.trim());
}

function loadStoredColor(): string {
    try {
        const s = localStorage.getItem(BBOX_COLOR_STORAGE_KEY);
        if (s && isHexColor(s)) return s.trim();
    } catch {
        /* ignore */
    }
    return DEFAULT_BBOX_OVERLAY_COLOR;
}

function clampNumber(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value));
}

function sanitizeStreamPathOverlayPrefs(
    raw: Partial<StreamPathOverlayPreferences> | null | undefined,
): StreamPathOverlayPreferences {
    const defaults = DEFAULT_STREAM_PATH_OVERLAY_PREFS;
    const pathColor =
        typeof raw?.pathColor === "string" && isHexColor(raw.pathColor)
            ? raw.pathColor.trim().toLowerCase()
            : defaults.pathColor;

    return {
        heightM: clampNumber(Number(raw?.heightM ?? defaults.heightM), 0.1, 1.5),
        pitchDeg: clampNumber(Number(raw?.pitchDeg ?? defaults.pitchDeg), 0, 45),
        hfovDeg: clampNumber(Number(raw?.hfovDeg ?? defaults.hfovDeg), 40, 140),
        vfovDeg: clampNumber(Number(raw?.vfovDeg ?? defaults.vfovDeg), 25, 140),
        forwardOffsetM: clampNumber(
            Number(raw?.forwardOffsetM ?? defaults.forwardOffsetM),
            -0.3,
            0.5,
        ),
        pathColor,
        lineWidth: clampNumber(Number(raw?.lineWidth ?? defaults.lineWidth), 1, 12),
        opacity: clampNumber(Number(raw?.opacity ?? defaults.opacity), 0.2, 1),
    };
}

function loadStoredStreamPathOverlay(): StreamPathOverlayPreferences {
    try {
        const raw = localStorage.getItem(STREAM_PATH_STORAGE_KEY);
        if (!raw) {
            return DEFAULT_STREAM_PATH_OVERLAY_PREFS;
        }
        return sanitizeStreamPathOverlayPrefs(JSON.parse(raw));
    } catch {
        return DEFAULT_STREAM_PATH_OVERLAY_PREFS;
    }
}

function persistStreamPathOverlay(prefs: StreamPathOverlayPreferences): void {
    try {
        localStorage.setItem(STREAM_PATH_STORAGE_KEY, JSON.stringify(prefs));
    } catch {
        /* ignore */
    }
}

type StreamPathOverlayField = keyof StreamPathOverlayPreferences;

type AccessibilityPreferencesValue = {
    colorMode: ColorModePreference;
    resolvedColorMode: ResolvedColorMode;
    setColorMode: (mode: ColorModePreference) => void;
    bboxOverlayColor: string;
    setBboxOverlayColor: (color: string) => void;
    streamPathOverlay: StreamPathOverlayPreferences;
    setStreamPathOverlayField: <K extends StreamPathOverlayField>(
        key: K,
        value: StreamPathOverlayPreferences[K],
    ) => void;
    resetStreamPathOverlay: () => void;
    streamPathCameraConfig: StreamPathCameraConfig;
};

const AccessibilityPreferencesContext =
    createContext<AccessibilityPreferencesValue | null>(null);
AccessibilityPreferencesContext.displayName = "AccessibilityPreferences";

export const useAccessibilityPreferences = (): AccessibilityPreferencesValue => {
    const ctx = useContext(AccessibilityPreferencesContext);
    if (!ctx) {
        throw new Error(
            "useAccessibilityPreferences must be used within AccessibilityPreferencesProvider"
        );
    }
    return ctx;
};

export const AccessibilityPreferencesProvider: FC<{ children: ReactNode }> = ({
    children,
}) => {
    const { selectedARC } = useARC();
    const host = selectedARC?.host ?? null;
    const isSecure = useIsHttpSecure();
    const arcId = useFleetArcId();
    const [colorMode, setColorModeState] = useState<ColorModePreference>(loadStoredColorMode);
    const [resolvedColorMode, setResolvedColorMode] = useState<ResolvedColorMode>(() =>
        resolveColorMode(loadStoredColorMode()),
    );
    const [bboxOverlayColor, setBboxOverlayColorState] = useState(loadStoredColor);
    const [streamPathOverlay, setStreamPathOverlayState] = useState(loadStoredStreamPathOverlay);
    const lastPersistedRef = useRef<string>("");

    useEffect(() => {
        const resolved = resolveColorMode(colorMode);
        setResolvedColorMode(resolved);
        applyResolvedColorMode(resolved);

        if (colorMode !== "system" || typeof window === "undefined" || !window.matchMedia) {
            return undefined;
        }

        const mq = window.matchMedia("(prefers-color-scheme: light)");
        const onChange = () => {
            const next = resolveColorMode("system");
            setResolvedColorMode(next);
            applyResolvedColorMode(next);
        };
        if (typeof mq.addEventListener === "function") {
            mq.addEventListener("change", onChange);
            return () => mq.removeEventListener("change", onChange);
        }
        mq.addListener(onChange);
        return () => mq.removeListener(onChange);
    }, [colorMode]);

    useEffect(() => {
        if (!host) return;
        let cancelled = false;
        const loadRemote = async () => {
            try {
                const url = getBboxServiceUrl(host, "settings", isSecure, arcId);
                if (!url) return;
                const res = await authedFetch(url);
                if (!res.ok) return;
                const payload = (await res.json()) as {
                    bbox_overlay_color?: string;
                };
                const remoteColor = payload.bbox_overlay_color;
                if (!cancelled && remoteColor && isHexColor(remoteColor)) {
                    const normalized = remoteColor.trim().toLowerCase();
                    lastPersistedRef.current = normalized;
                    setBboxOverlayColorState(normalized);
                    try {
                        localStorage.setItem(BBOX_COLOR_STORAGE_KEY, normalized);
                    } catch {
                        /* ignore */
                    }
                }
            } catch {
                /* ignore */
            }
        };
        void loadRemote();
        return () => {
            cancelled = true;
        };
    }, [host, isSecure, arcId]);

    const setColorMode = useCallback((mode: ColorModePreference) => {
        setColorModeState(mode);
        persistColorMode(mode);
    }, []);

    const setBboxOverlayColor = useCallback((color: string) => {
        const c = color.trim();
        if (!isHexColor(c)) return;
        const normalized = c.toLowerCase();
        setBboxOverlayColorState(normalized);
        try {
            localStorage.setItem(BBOX_COLOR_STORAGE_KEY, normalized);
        } catch {
            /* ignore */
        }
        if (!host || lastPersistedRef.current === normalized) return;
        lastPersistedRef.current = normalized;
        const url = getBboxServiceUrl(host, "settings", isSecure, arcId);
        if (!url) return;
        void authedFetch(url, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ bbox_overlay_color: normalized }),
        }).catch(() => {
            /* ignore */
        });
    }, [host, isSecure, arcId]);

    const setStreamPathOverlayField = useCallback(
        <K extends StreamPathOverlayField>(key: K, value: StreamPathOverlayPreferences[K]) => {
            setStreamPathOverlayState((prev) => {
                const next = sanitizeStreamPathOverlayPrefs({ ...prev, [key]: value });
                persistStreamPathOverlay(next);
                return next;
            });
        },
        [],
    );

    const resetStreamPathOverlay = useCallback(() => {
        const next = sanitizeStreamPathOverlayPrefs(DEFAULT_STREAM_PATH_OVERLAY_PREFS);
        setStreamPathOverlayState(next);
        persistStreamPathOverlay(next);
    }, []);

    const streamPathCameraConfig = useMemo(
        () => streamPathOverlayPrefsToCameraConfig(streamPathOverlay),
        [streamPathOverlay],
    );

    const value = useMemo(
        () => ({
            colorMode,
            resolvedColorMode,
            setColorMode,
            bboxOverlayColor,
            setBboxOverlayColor,
            streamPathOverlay,
            setStreamPathOverlayField,
            resetStreamPathOverlay,
            streamPathCameraConfig,
        }),
        [
            colorMode,
            resolvedColorMode,
            setColorMode,
            bboxOverlayColor,
            setBboxOverlayColor,
            streamPathOverlay,
            setStreamPathOverlayField,
            resetStreamPathOverlay,
            streamPathCameraConfig,
        ],
    );

    return (
        <AccessibilityPreferencesContext.Provider value={value}>
            {children}
        </AccessibilityPreferencesContext.Provider>
    );
};
